import { parseLocale } from "../i18n/LocaleContext"

const DATE_FORMAT = /^(\d{4})-(\d{2})-(\d{2})$/

export function parseDateString(dateStr: string) {
  const matchResult = DATE_FORMAT.exec(dateStr)
  if (matchResult && Date.parse(dateStr)) {
    return {
      year: matchResult[1],
      month: matchResult[2],
      day: matchResult[3],
    }
  }
  return {
    year: "",
    month: "",
    day: "",
  }
}

/**
 * Accepts numbers or letter abbreviations depending on the locale
 * @param str
 * @returns the month number in string, or "" if `str` is invalid
 */
export function parseMonth(str: string, locale: string) {
  if (!str) {
    return ""
  }
  if (/^\d{1,2}$/.test(str)) {
    const month = Number(str)
    if (month > 12 || month < 1) {
      return ""
    } else {
      return str
    }
  }
  const parsedLocale = parseLocale(locale)
  if (!parsedLocale) {
    return ""
  }

  str = normaliseMonthInput(str)

  const { language, region } = parsedLocale
  if (str.length === 2 && ["CA", "AU", "NZ"].includes(region)) {
    return EN_2_LETTER_MONTH_DICT[str] || ""
  }
  if (EN_MONTH_DICT[str]) {
    return EN_MONTH_DICT[str]
  }
  switch (language) {
    case "es": {
      if (region === "ES" || region === "MX") {
        if (ES_MONTH_DICT[str]) {
          return ES_MONTH_DICT[str]
        }
      }
      return ""
    }
    case "pt": {
      if (region === "BR") {
        if (PT_MONTH_DICT[str]) {
          return PT_MONTH_DICT[str]
        }
      }
      return ""
    }
    case "fr": {
      if (region === "FR" || region === "BE") {
        if (FR_MONTH_DICT[str]) {
          return FR_MONTH_DICT[str]
        }
      }
      return ""
    }
    case "de": {
      if (region === "DE") {
        if (DE_MONTH_DICT[str]) {
          return DE_MONTH_DICT[str]
        }
      }
      return ""
    }
    case "pl": {
      if (region === "PL") {
        if (PL_MONTH_DICT[str]) {
          return PL_MONTH_DICT[str]
        }
      }
    }
    default: return ""
  }
}

function normaliseMonthInput(str: string) {
  return str
    .normalize("NFD") // Separates characters into base letter and diacritic mark
    .replace(/[\u0300-\u036f]/g, "") // Removes the diacritic marks (Unicode range)
    .toLowerCase()
}

export function getMaxDayForYearMonth(year: number, month: number) {
  /**
   * We exploit the special behavior of Date to get the result.
   *
   * From MDN:
   * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date
   *
   * When a segment overflows or underflows its expected range,
   * it usually "carries over to" or "borrows from" the higher segment.
   * For example, if the month is set to 12 (months are zero-based, so December is 11),
   * it becomes the January of the next year. If the day of month is set to 0,
   * it becomes the last day of the previous month. This also applies to dates
   * specified with the date time string format.
   */
  const d = new Date(year, month, 0)
  return d.getDate()
}

export function getDateComponentOrder(locale: string): "ymd" | "mdy" | "dmy" {
  const region = parseLocale(locale)?.region || ""
  if (region === "US") {
    return "mdy"
  } else if (["CA", "JP", "CN", "KR", "SE"].includes(region)) {
    return "ymd"
  } else if ([
    "MX", "BR", "ES", "FR", "IT", "GB", "DE", "PL", "IE", "BE", "TR", "SA", "AE",
    "IN", "SG", "AU", "EG", "ZA",
  ].includes(region)) {
    return "dmy"
  }
  return "mdy"
}

type NumericMonth = "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9" | "10" | "11" | "12"

const EN_MONTH_DICT: Record<string, NumericMonth> = {
  jan: "1",
  feb: "2",
  mar: "3",
  apr: "4",
  may: "5",
  jun: "6",
  jul: "7",
  aug: "8",
  sep: "9",
  oct: "10",
  nov: "11",
  dec: "12",
}

const EN_2_LETTER_MONTH_DICT: Record<string, NumericMonth> = {
  ja: "1",
  fe: "2",
  mr: "3",
  al: "4",
  ma: "5",
  jn: "6",
  jl: "7",
  au: "8",
  se: "9",
  oc: "10",
  no: "11",
  de: "12",
}

const ES_MONTH_DICT: Record<string, NumericMonth> = {
  ene: "1",
  feb: "2",
  mar: "3",
  abr: "4",
  may: "5",
  jun: "6",
  jul: "7",
  ago: "8",
  sep: "9",
  oct: "10",
  nov: "11",
  dic: "12",
}

const PT_MONTH_DICT: Record<string, NumericMonth> = {
  jan: "1",
  fev: "2",
  mar: "3",
  abr: "4",
  maio: "5",
  jun: "6",
  jul: "7",
  ago: "8",
  set: "9",
  out: "10",
  nov: "11",
  dez: "12",
}

const FR_MONTH_DICT: Record<string, NumericMonth> = {
  janv: "1",
  fevr: "2",
  mars: "3",
  avr: "4",
  mai: "5",
  juin: "6",
  juil: "7",
  aout: "8",
  sept: "9",
  oct: "10",
  nov: "11",
  dec: "12",
}

const DE_MONTH_DICT: Record<string, NumericMonth> = {
  jan: "1",
  feb: "2",
  mrz: "3",
  apr: "4",
  mai: "5",
  jun: "6",
  jul: "7",
  aug: "8",
  sep: "9",
  okt: "10",
  nov: "11",
  dez: "12",
}

const PL_MONTH_DICT: Record<string, NumericMonth> = {
  sty: "1",
  lut: "2",
  mar: "3",
  kwi: "4",
  maj: "5",
  cze: "6",
  lip: "7",
  sie: "8",
  wrz: "9",
  paz: "10",
  lis: "11",
  gru: "12",
}
